"use client"

import { useCallback } from "react"
import Image from "next/image"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Slider } from "@/components/ui/slider"
import { Separator } from "@/components/ui/separator"
import {
  Trash2,
  Maximize,
  AlignHorizontalJustifyCenter,
  AlignVerticalJustifyCenter,
  MoveUp,
  MoveDown,
  RotateCw,
  FlipHorizontal,
  FlipVertical,
  RefreshCcw,
  ImageIcon,
} from "lucide-react"
import type { ImageItem, Position, Size } from "./types"

interface PropertiesPanelProps {
  item: ImageItem | null
  pageWidth: number
  pageHeight: number
  margins: number
  disabled?: boolean
  onPositionChange: (id: string, position: Position) => void
  onSizeChange: (id: string, size: Size) => void
  onRotationChange: (id: string, rotation: number) => void
  onOpacityChange?: (id: string, opacity: number) => void
  onFlipX?: (id: string) => void
  onFlipY?: (id: string) => void
  onDelete: (id: string) => void
  onBringForward: (id: string) => void
  onSendBackward: (id: string) => void
}

export function PropertiesPanel({
  item,
  pageWidth,
  pageHeight,
  margins,
  disabled = false,
  onPositionChange,
  onSizeChange,
  onRotationChange,
  onOpacityChange,
  onFlipX,
  onFlipY,
  onDelete,
  onBringForward,
  onSendBackward,
}: PropertiesPanelProps) {
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const handleFitToPage = useCallback(() => {
    if (!item) return
    const contentWidth = pageWidth - margins * 2
    const contentHeight = pageHeight - margins * 2
    const aspectRatio = item.originalWidth / item.originalHeight

    let newWidth = contentWidth
    let newHeight = contentWidth / aspectRatio

    if (newHeight > contentHeight) {
      newHeight = contentHeight
      newWidth = contentHeight * aspectRatio
    }

    onSizeChange(item.id, { width: newWidth, height: newHeight })
    onPositionChange(item.id, {
      x: margins + (contentWidth - newWidth) / 2,
      y: margins + (contentHeight - newHeight) / 2,
    })
  }, [item, pageWidth, pageHeight, margins, onSizeChange, onPositionChange])

  const handleCenterHorizontal = useCallback(() => {
    if (!item) return
    onPositionChange(item.id, {
      x: (pageWidth - item.size.width) / 2,
      y: item.position.y,
    })
  }, [item, pageWidth, onPositionChange])

  const handleCenterVertical = useCallback(() => {
    if (!item) return
    onPositionChange(item.id, {
      x: item.position.x,
      y: (pageHeight - item.size.height) / 2,
    })
  }, [item, pageHeight, onPositionChange])

  const handleRotate90 = useCallback(() => {
    if (!item) return
    const newRotation = (item.rotation + 90) % 360
    onRotationChange(item.id, newRotation)
  }, [item, onRotationChange])

  const handleResetSize = useCallback(() => {
    if (!item) return
    onSizeChange(item.id, {
      width: item.originalWidth,
      height: item.originalHeight,
    })
  }, [item, onSizeChange])

  if (!item) {
    return (
      <div className="p-6 text-center text-muted-foreground flex flex-col items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
          <ImageIcon className="h-6 w-6" />
        </div>
        <div>
          <p className="text-sm font-medium">No image selected</p>
          <p className="text-xs mt-1">Click an image on the canvas to edit</p>
        </div>
      </div>
    )
  }

  const aspectRatio = item.originalWidth / item.originalHeight
  const currentScale = Math.round((item.size.width / item.originalWidth) * 100)
  const opacity = item.opacity ?? 100

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-lg border overflow-hidden flex-shrink-0 bg-muted">
          <Image
            src={item.src}
            alt={`${item.fileName} thumbnail`}
            width={48}
            height={48}
            unoptimized
            className="w-full h-full object-cover"
          />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-medium text-sm truncate" title={item.fileName}>
            {item.fileName}
          </h3>
          <p className="text-xs text-muted-foreground">
            {item.originalWidth} x {item.originalHeight}px
          </p>
          <p className="text-xs text-muted-foreground">
            {formatSize(item.fileSize)}
          </p>
        </div>
      </div>

      <Separator />

      <div className="space-y-3">
        <Label className="text-xs font-medium">Transform</Label>
        <div className="grid grid-cols-5 gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={handleFitToPage}
            disabled={disabled}
            title="Fit to page (F)"
          >
            <Maximize className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={handleCenterHorizontal}
            disabled={disabled}
            title="Center horizontally (H)"
          >
            <AlignHorizontalJustifyCenter className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={handleCenterVertical}
            disabled={disabled}
            title="Center vertically (V)"
          >
            <AlignVerticalJustifyCenter className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={handleRotate90}
            disabled={disabled}
            title="Rotate 90° (R)"
          >
            <RotateCw className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={handleResetSize}
            disabled={disabled}
            title="Reset to original size"
          >
            <RefreshCcw className="h-4 w-4" />
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-1">
          <Button
            variant={item.flipX ? "secondary" : "outline"}
            size="sm"
            className="h-8"
            onClick={() => onFlipX?.(item.id)}
            disabled={disabled || !onFlipX}
          >
            <FlipHorizontal className="h-4 w-4 mr-1" />
            Flip H
          </Button>
          <Button
            variant={item.flipY ? "secondary" : "outline"}
            size="sm"
            className="h-8"
            onClick={() => onFlipY?.(item.id)}
            disabled={disabled || !onFlipY}
          >
            <FlipVertical className="h-4 w-4 mr-1" />
            Flip V
          </Button>
        </div>
      </div>

      <Separator />

      <div className="space-y-3">
        <Label className="text-xs font-medium">Position (pt)</Label>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">X</Label>
            <Input
              type="number"
              value={Math.round(item.position.x)}
              onChange={(e) =>
                onPositionChange(item.id, { ...item.position, x: Number(e.target.value) })
              }
              disabled={disabled}
              className="h-8"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Y</Label>
            <Input
              type="number"
              value={Math.round(item.position.y)}
              onChange={(e) =>
                onPositionChange(item.id, { ...item.position, y: Number(e.target.value) })
              }
              disabled={disabled}
              className="h-8"
            />
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <Label className="text-xs font-medium">Size (pt)</Label>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Width</Label>
            <Input
              type="number"
              value={Math.round(item.size.width)}
              onChange={(e) => {
                const newWidth = Number(e.target.value)
                onSizeChange(item.id, {
                  width: newWidth,
                  height: newWidth / aspectRatio,
                })
              }}
              disabled={disabled}
              className="h-8"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Height</Label>
            <Input
              type="number"
              value={Math.round(item.size.height)}
              onChange={(e) => {
                const newHeight = Number(e.target.value)
                onSizeChange(item.id, {
                  width: newHeight * aspectRatio,
                  height: newHeight,
                })
              }}
              disabled={disabled}
              className="h-8"
            />
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-medium">Scale</Label>
          <span className="text-xs text-muted-foreground">{currentScale}%</span>
        </div>
        <Slider
          value={[currentScale]}
          min={10}
          max={200}
          step={5}
          onValueChange={([value]) => {
            const scale = value / 100
            onSizeChange(item.id, {
              width: item.originalWidth * scale,
              height: item.originalHeight * scale,
            })
          }}
          disabled={disabled}
        />
      </div>

      {onOpacityChange && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-medium">Opacity</Label>
            <span className="text-xs text-muted-foreground">{opacity}%</span>
          </div>
          <Slider
            value={[opacity]}
            min={10}
            max={100}
            step={5}
            onValueChange={([value]) => onOpacityChange(item.id, value)}
            disabled={disabled}
          />
        </div>
      )}

      <Separator />

      <div className="space-y-3">
        <Label className="text-xs font-medium">Layer Order</Label>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => onBringForward(item.id)}
            disabled={disabled}
          >
            <MoveUp className="h-4 w-4 mr-1" />
            Forward
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => onSendBackward(item.id)}
            disabled={disabled}
          >
            <MoveDown className="h-4 w-4 mr-1" />
            Backward
          </Button>
        </div>
      </div>

      <Separator />

      <Button
        variant="destructive"
        size="sm"
        className="w-full"
        onClick={() => onDelete(item.id)}
        disabled={disabled}
      >
        <Trash2 className="h-4 w-4 mr-1" />
        Remove Image
      </Button>
    </div>
  )
}
